// HT-4 · 読み直すと役立つ書類（管理者が選んだ書類だけを読み直す。自動実行はしない）
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowsClockwise, ArrowSquareOut, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { LEARN, authHeader, detailOf, ROLE_JA, Panel, Pill, Empty, Help, Spinner } from './shared';
import RereadHistory from './RereadHistory';

const MAX_PER_RUN = 20;

function ResultRow({ r }) {
  return (
    <div className="rounded-md border border-zinc-200 px-3 py-2"
      data-testid={`reread-result-${r.document_id}`}>
      <p className="flex flex-wrap items-center gap-1.5">
        <Pill tone={r.ok ? 'teal' : 'rose'}>{r.ok ? '読み直しました' : '読み直せませんでした'}</Pill>
        <span className="text-[12px] font-medium text-zinc-900">{r.title || r.document_id}</span>
        {r.ok && <Pill>学習内容の適用 {r.applied_count ?? 0}件</Pill>}
        {r.ok && r.learning_version && <Pill>バージョン {r.learning_version}</Pill>}
      </p>
      {(r.effects_ja || []).length > 0 && (
        <ul className="list-disc pl-5 pt-1 text-[11.5px] text-zinc-700">
          {r.effects_ja.map((t) => <li key={t}>{t}</li>)}
        </ul>
      )}
      {r.ok && (r.effects_ja || []).length === 0 && (
        <p className="pt-1 text-[11.5px] text-zinc-500">
          この書類では、学習内容は使われませんでした。
        </p>
      )}
      {!r.ok && (
        <p className="pt-1 text-[11.5px] text-rose-800">
          <Warning size={11} className="mr-1 inline" />{r.error_ja || '原因は記録を確認してください'}
        </p>
      )}
    </div>
  );
}

export default function Reread({ onChanged }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${LEARN}/reread-candidates`, { headers: authHeader() });
      setD(r.data);
      setPicked([]);
      setResults(null);
      setErr('');
    } catch (e) {
      setErr(detailOf(e, '読み直しの候補を取得できませんでした'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = d?.rows || [];
  const toggle = (id) => setPicked((prev) => {
    if (prev.includes(id)) return prev.filter((x) => x !== id);
    if (prev.length >= MAX_PER_RUN) {
      toast.error(`一度に読み直せるのは${MAX_PER_RUN}件までです`);
      return prev;
    }
    return [...prev, id];
  });

  const selectAll = () => {
    const ids = rows.slice(0, MAX_PER_RUN).map((r) => r.document_id);
    if (rows.length > MAX_PER_RUN) {
      toast.info(`先頭の${MAX_PER_RUN}件を選びました（残りは次の回に読み直せます）`);
    }
    setPicked(ids);
  };

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${LEARN}/reread`, { document_ids: picked },
        { headers: authHeader() });
      toast.success(r.data.summary_ja);
      await load();
      setResults(r.data);
      setHistoryKey((v) => v + 1);
      onChanged?.();
    } catch (e) {
      toast.error(detailOf(e, '読み直せませんでした'));
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <Panel title="読み直すと役立つ書類" testId="learn-reread">
        <p className="text-[11.5px] text-amber-900" data-testid="reread-error">{err}</p>
      </Panel>
    );
  }
  if (!d) return null;

  return (
    <Panel title="読み直すと役立つ書類" testId="learn-reread"
      hint="今の学習内容で読み直すと改善しそうな書類です。過去の書類は自動では書き換えません。選んだ書類だけを読み直します。"
      action={(
        <Button size="sm" variant="outline" className="h-9 text-[12px]" onClick={load}
          disabled={busy} data-testid="reread-refresh">
          <ArrowsClockwise size={12} className="mr-1" />一覧を更新
        </Button>
      )}>
      <p className="flex flex-wrap gap-1.5" data-testid="reread-summary">
        <Pill tone={d.active ? 'teal' : 'zinc'} testId="reread-version">
          {d.active ? `バージョン ${d.learning_version}` : '有効な学習内容なし'}
        </Pill>
        <Pill>読み直しの候補 {d.count ?? rows.length}件</Pill>
        {(d.profiles_ja || []).length > 0 && <Pill>対象 {d.profiles_ja.join(' / ')}</Pill>}
        <Pill>一度に {MAX_PER_RUN}件まで</Pill>
      </p>

      <p className="pt-2 text-[11.5px] leading-relaxed text-zinc-600" data-testid="reread-notice">
        {d.notice_ja}
      </p>
      <div className="pt-1.5">
        <Help label="読み直し" testId="reread-help">
          読み直しは、管理者が選んだ書類にだけ実行します。読み取りのしくみ（モデル・プロンプト）は
          変わりません。学習内容は手がかりとしてだけ渡され、値は候補として提示されます。
          確認済み・修正済みの値は上書きされません。
        </Help>
      </div>

      {rows.length === 0 ? (
        <div className="pt-3">
          <Empty testId="reread-empty">{d.message_ja}</Empty>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 pt-3">
            <Button size="sm" variant="outline" className="h-9 text-[12px]" onClick={selectAll}
              disabled={busy} data-testid="reread-select-all">すべて選択</Button>
            <Button size="sm" variant="outline" className="h-9 text-[12px]"
              onClick={() => setPicked([])} disabled={busy || picked.length === 0}
              data-testid="reread-clear">選択を解除</Button>
            <Button className="h-10 bg-teal-700 text-[12.5px] hover:bg-teal-800"
              onClick={run} disabled={busy || picked.length === 0} data-testid="reread-run">
              {busy ? <Spinner /> : null}
              <span className={busy ? 'ml-1' : ''}>選んだ書類を読み直す（{picked.length}件）</span>
            </Button>
          </div>

          <div className="space-y-1.5 pt-3">
            {rows.map((r) => (
              <div key={r.document_id}
                className="rounded-md border border-zinc-200 px-3 py-2"
                data-testid={`reread-row-${r.document_id}`}>
                <div className="flex items-start gap-2.5">
                  <Checkbox checked={picked.includes(r.document_id)}
                    onCheckedChange={() => toggle(r.document_id)}
                    className="mt-0.5 after:-inset-2.5"
                    aria-label={`${r.title || r.document_id} を読み直す対象にする`}
                    data-testid={`reread-check-${r.document_id}`} />
                  <div className="min-w-0 flex-1">
                    <p className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[12.5px] font-medium text-zinc-900">
                        {r.title || r.document_id}
                      </span>
                      {r.current_learning_version
                        ? <Pill>今の読み取り: バージョン {r.current_learning_version}</Pill>
                        : <Pill tone="amber">学習内容を使わずに読み取り</Pill>}
                      {r.read_at && (
                        <span className="text-[11px] text-zinc-500">
                          読み取り {String(r.read_at).slice(0, 10)}
                        </span>
                      )}
                    </p>
                    <ul className="list-disc pl-5 pt-1 text-[11.5px] text-zinc-700"
                      data-testid={`reread-reasons-${r.document_id}`}>
                      {(r.reasons_ja || []).map((t) => <li key={t}>{t}</li>)}
                    </ul>
                    {(r.unreadable_fields || []).length > 0 && (
                      <p className="pt-0.5 text-[11px] text-amber-900">
                        判読不能の項目: {r.unreadable_fields
                          .map((f) => ROLE_JA[f] || f).join(' / ')}
                      </p>
                    )}
                  </div>
                  <a href={`/documents?open=${r.document_id}`} target="_blank" rel="noreferrer"
                    className="inline-flex min-h-[32px] shrink-0 items-center gap-1 text-[11.5px] text-teal-700 hover:underline"
                    data-testid={`reread-open-${r.document_id}`}>
                    <ArrowSquareOut size={12} />書類を開く
                  </a>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {results && (
        <div className="space-y-1.5 pt-4" data-testid="reread-results">
          <p className="text-[12px] font-medium text-zinc-800">読み直しの結果</p>
          <p className="text-[11.5px] text-zinc-600" data-testid="reread-results-summary">
            {results.summary_ja} · {results.notice_ja}
          </p>
          {(results.results || []).map((r) => <ResultRow key={r.document_id} r={r} />)}
        </div>
      )}

      <RereadHistory reloadKey={historyKey} />
    </Panel>
  );
}
